import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Megaphone, Send, Eye, RefreshCw, Users } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const ROLES = [
  { v: 'all', label: 'All users' },
  { v: 'rider', label: 'Riders' },
  { v: 'driver', label: 'Drivers' },
  { v: 'biker', label: 'Bikers' },
  { v: 'bus_operator', label: 'Bus operators' },
];

const CATEGORIES = [
  { v: 'marketing', label: 'Marketing', tint: 'bg-pink-50 text-pink-700 border-pink-200' },
  { v: 'rides', label: 'Rides', tint: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { v: 'bus', label: 'Bus', tint: 'bg-amber-50 text-amber-700 border-amber-200' },
  { v: 'packages', label: 'Packages', tint: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

const AdminPushBroadcast = () => {
  const [form, setForm] = useState({
    title: '',
    body: '',
    category: 'marketing',
    role: 'rider',
    city: '',
    active_days: '',
  });
  const [eligible, setEligible] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchHistory = async () => {
    try {
      const { data } = await api.get('/admin/push/broadcasts?limit=20');
      setHistory(data.items || []);
    } catch { setHistory([]); }
  };
  useEffect(() => { fetchHistory(); }, []);

  const buildBody = () => ({
    title: form.title,
    body: form.body,
    category: form.category,
    role: form.role,
    city: form.city || null,
    active_days: form.active_days ? parseInt(form.active_days, 10) : null,
  });

  const preview = async () => {
    if (!form.title || !form.body) {
      toast({ title: 'Title and body required', variant: 'destructive' });
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post('/admin/push/broadcast', { ...buildBody(), dry_run: true });
      setEligible(data.eligible);
      toast({ title: `${data.eligible} user(s) match` });
    } catch (err) {
      toast({ title: 'Preview failed', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    if (!form.title || !form.body) {
      toast({ title: 'Title and body required', variant: 'destructive' });
      return;
    }
    if (!window.confirm(`Send this push to ${eligible ?? '?'} user(s)?`)) return;
    setSending(true);
    try {
      const { data } = await api.post('/admin/push/broadcast', { ...buildBody(), dry_run: false });
      toast({
        title: 'Broadcast sent',
        description: `Eligible ${data.eligible}, delivered ${data.sent}`,
      });
      setForm({ ...form, title: '', body: '' });
      setEligible(null);
      fetchHistory();
    } catch (err) {
      toast({ title: 'Send failed', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-push-broadcast-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Megaphone className="h-6 w-6 text-pink-500" /> Push Broadcast
            </h1>
            <p className="text-sm text-gray-500">
              Fan out a native push to any cohort. Respects each user&apos;s push preferences (<code>sos</code> always delivered).
            </p>
          </div>
          <Button variant="outline" onClick={fetchHistory} data-testid="broadcast-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Composer */}
        <Card className="p-5 space-y-4" data-testid="broadcast-composer-card">
          <div>
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value, })}
              placeholder="🌧️ Surge in Lusaka — earn more now"
              maxLength={120}
              data-testid="broadcast-title-input"
            />
          </div>

          <div>
            <Label>Message</Label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Rides are spiking across Lusaka East. Go online now for 1.5x base fares."
              maxLength={240}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-20"
              data-testid="broadcast-body-input"
            />
            <p className="text-xs text-gray-500 mt-1">{form.body.length}/240</p>
          </div>

          <div>
            <Label>Category (user&apos;s preference gate)</Label>
            <div className="flex gap-2 mt-1 flex-wrap">
              {CATEGORIES.map((c) => (
                <button
                  key={c.v}
                  type="button"
                  onClick={() => setForm({ ...form, category: c.v })}
                  className={`px-3 py-1.5 rounded-full border text-xs font-medium ${
                    form.category === c.v ? c.tint : 'bg-white text-gray-600 border-gray-300'
                  }`}
                  data-testid={`broadcast-category-${c.v}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label>Audience role</Label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                data-testid="broadcast-role-select"
              >
                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <Label>City (matches last ride pickup)</Label>
              <Input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Lusaka · Kasama · Ndola"
                data-testid="broadcast-city-input"
              />
            </div>
            <div>
              <Label>Active within (days)</Label>
              <Input
                type="number" min="1" max="365"
                value={form.active_days}
                onChange={(e) => setForm({ ...form, active_days: e.target.value })}
                placeholder="30"
                data-testid="broadcast-activedays-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between border-t pt-3 gap-3 flex-wrap">
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <Users className="h-4 w-4" />
              {eligible === null ? 'Run preview to see audience' : (
                <span><span className="font-semibold">{eligible}</span> user(s) match</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={preview}
                disabled={previewing}
                data-testid="broadcast-preview-btn"
              >
                <Eye className="h-4 w-4 mr-1" /> {previewing ? 'Counting…' : 'Preview count'}
              </Button>
              <Button
                onClick={send}
                disabled={sending || eligible === 0}
                data-testid="broadcast-send-btn"
                className="bg-pink-600 hover:bg-pink-700 text-white"
              >
                <Send className="h-4 w-4 mr-1" /> {sending ? 'Sending…' : 'Send now'}
              </Button>
            </div>
          </div>
        </Card>

        {/* History */}
        <Card className="p-5" data-testid="broadcast-history-card">
          <h3 className="font-semibold mb-3 text-sm">Recent broadcasts</h3>
          {history.length === 0 ? (
            <p className="text-xs text-gray-500">No broadcasts yet.</p>
          ) : (
            <ul className="divide-y text-sm">
              {history.map((h) => (
                <li key={h.id} className="py-2 flex items-center justify-between gap-3" data-testid={`broadcast-row-${h.id}`}>
                  <div>
                    <p className="font-medium">{h.title}</p>
                    <p className="text-xs text-gray-500">
                      {(h.filters?.role || 'all')} · {h.filters?.city || 'any city'} · {h.filters?.active_days ? `active ${h.filters.active_days}d` : 'all activity'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold">{h.sent}/{h.eligible}</p>
                    <p className="text-[10px] uppercase tracking-wide text-gray-400">{h.status}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminPushBroadcast;
